/**
 * MARCAS QUE ARGEMIRO PROPONE (ARG-MARCAJE-PROPONE-T1; API: nexo-api 329, contrato de Argy-A #18682).
 *
 * Argemiro mira los dias incompletos y propone la marca que falta (entrada o salida, con hora sugerida del horario o
 * del promedio de la persona). NADA se escribe sin el jefe: aqui el jefe ve cada propuesta, puede ajustar la hora y
 * la autoriza; la API deja la marca escrita con su nombre (sale del token) y el Nucleo recalcula.
 *
 * Puro: sin DOM ni red. La vista (presentation/shared/NxMarcasPropuestas.js) solo pinta lo que esto decide, y el guardia
 * check:marcas-propuestas lo prueba en node.
 */

export const TIPO_MARCA = Object.freeze({
  entrada: 'Entrada', salida: 'Salida', inicio_almuerzo: 'Salida a almuerzo', fin_almuerzo: 'Regreso de almuerzo',
  almuerzo_inicio: 'Salida a almuerzo', almuerzo_fin: 'Regreso de almuerzo'
});

/** "HH:mm:ss" | "HH:mm" | null -> "HH:mm" o "" (sin sugerida: el jefe la pone). */
export function horaCorta(h) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(h ?? ''));
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}

/** "2026-09-18T00:00:00" | "2026-09-18" -> "2026-09-18" (la fecha del dia, sin zona). */
export function fechaCorta(f) {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(f ?? ''));
  return m ? m[1] : '';
}

/**
 * La respuesta del GET a lo que la vista necesita. Tolera camelCase y PascalCase y la data envuelta o no.
 * @returns {{estado:'ok'|'vacio'|'error', causa?:string, desde?:string, hasta?:string, personas?:Array}}
 */
export function adaptarPropuestas(res, { env = '' } = {}) {
  if (!res || res.ok !== true) return { estado: 'error', causa: causaDelFallo(res, { env }) };
  const d = res.data?.data ?? res.data?.Data ?? res.data ?? {};
  const lista = d.propuestas ?? d.Propuestas;
  if (!Array.isArray(lista)) return { estado: 'error', causa: 'La API respondió algo que no supe leer.' };
  const propuestas = lista.map((p) => ({
    colaboradorId: String(p.colaboradorId ?? p.ColaboradorId ?? ''),
    nombre: String(p.nombre ?? p.Nombre ?? 'Sin nombre'),
    fecha: fechaCorta(p.fecha ?? p.Fecha),
    tipo: String(p.tipo ?? p.Tipo ?? ''),
    horaSugerida: horaCorta(p.horaSugerida ?? p.HoraSugerida),
    fuente: String(p.fuente ?? p.Fuente ?? ''),
    motivo: String(p.motivo ?? p.Motivo ?? '')
  })).filter((p) => p.colaboradorId && p.fecha && p.tipo);
  const desde = String(d.desde ?? d.Desde ?? ''), hasta = String(d.hasta ?? d.Hasta ?? '');
  if (!propuestas.length) return { estado: 'vacio', desde, hasta, causa: `Argemiro no tiene marcas por proponer entre ${desde} y ${hasta}: no hay días incompletos por completar.` };
  // Por persona, y dentro de cada una por fecha: el jefe revisa a una persona de corrido.
  const porPersona = new Map();
  for (const p of propuestas) {
    if (!porPersona.has(p.colaboradorId)) porPersona.set(p.colaboradorId, { colaboradorId: p.colaboradorId, nombre: p.nombre, propuestas: [] });
    porPersona.get(p.colaboradorId).propuestas.push(p);
  }
  const personas = [...porPersona.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  for (const x of personas) x.propuestas.sort((a, b) => a.fecha.localeCompare(b.fecha) || a.tipo.localeCompare(b.tipo));
  return { estado: 'ok', desde, hasta, total: propuestas.length, personas };
}

/** Por que no se pudo leer, dicho por causa (F3 del molde): no es lo mismo no tener permiso que estar en dev. */
export function causaDelFallo(res, { env = '' } = {}) {
  const code = res?.code;
  if (code === 403 || code === 'FORBIDDEN') return 'Solo el jefe o el administrador del negocio ve las marcas por autorizar.';
  if (code === 404) return 'La API todavía no tiene publicadas las marcas propuestas.';
  if (code === 400) return res?.error || 'El rango de fechas no es válido (a lo sumo 92 días).';
  if (env === 'dev') return 'Estás en el panel de desarrollo: pregunta a la API local, que no tiene esta consulta. En producción sí existe.';
  return 'No pudimos hablar con la API ahora. Intenta de nuevo en un momento.';
}

/**
 * El cuerpo del POST, en un solo sitio y en camelCase (ASP.NET no liga snake_case, F5-bis). La hora se manda solo si
 * el jefe la puso o la dejo sugerida; sin hora, la API usa la sugerida o responde HORA_REQUERIDA.
 */
export function cuerpoAutorizar(p, hora) {
  const h = horaCorta(hora);
  return { colaboradorId: p.colaboradorId, fecha: p.fecha, tipo: p.tipo, ...(h ? { hora: h } : {}) };
}

/** Lo que se le dice al jefe despues de autorizar, por codigo de la API. */
export function mensajeAutorizar(res) {
  if (res?.ok === true) return { ok: true, texto: 'Autorizada: la marca quedó escrita con tu nombre.' };
  const c = res?.details?.code ?? res?.details?.Code;
  if (c === 'NO_ES_PROPUESTA') return { ok: false, texto: 'Ya no es una propuesta: alguien marcó ese día o ya se autorizó. Actualiza la lista.' };
  if (c === 'HORA_REQUERIDA') return { ok: false, texto: 'Esta propuesta no trae hora sugerida: pon la hora y vuelve a autorizar.' };
  if (c === 'HORA_INVALIDA') return { ok: false, texto: 'La hora va como HH:mm (por ejemplo 18:30).' };
  if (c === 'FALTAN_DATOS') return { ok: false, texto: 'Faltan datos de la propuesta. Actualiza la lista.' };
  if (res?.code === 403 || res?.code === 'FORBIDDEN') return { ok: false, texto: 'Solo el jefe o el administrador puede autorizar.' };
  return { ok: false, texto: 'No se pudo autorizar ahora. Intenta de nuevo en un momento.' };
}
